import { ApolloError, isApolloError } from 'apollo-client';
import { GraphQLError as GQLError } from 'graphql';
import { IntlFormatters } from 'react-intl';

import { NonNullableObject } from '@rbi-ctg/frontend';
import { CardTypes } from 'state/payment/types';
import { filterMap } from 'utils/array';
import { GraphQLError } from 'utils/network';

import { GraphQLErrorCodes, IGraphqlErrorMap } from './types';

const mapErrorsWithCodes = filterMap<GQLError, NonNullableObject<GQLError>, IGQLParsedErrorCode>(
  err => !!err.extensions?.code,
  err => ({ errorCode: err.extensions.code, message: err.message } as IGQLParsedErrorCode)
);

/**
 * Given a GraphQL error, parse all the GraphQL error codes.
 */
export function parseGraphQLErrorCodes(error: GraphQLError | ApolloError): IGQLParsedErrorCode[] {
  if (isApolloError(error)) {
    return mapErrorsWithCodes(error.graphQLErrors);
  }
  return mapErrorsWithCodes(error.originalError || []);
}

/**
 * Given an array of graphql errors, do any errors have an extension with the given code
 *
 * @param graphQLErrors {GraphQLError[]} An array of Apollo GraphQL errors
 * @param code {GraphQLErrorCodes} The code
 */
export const graphQLErrorsIncludeExtensionCode = (
  graphQLErrors: ApolloError['graphQLErrors'] = [],
  code: GraphQLErrorCodes
) => graphQLErrors.some(graphQLError => graphQLError?.extensions?.code === code);

export interface IErrorDialog {
  title?: string;
  body?: string;
  link?: {
    key: string;
    to: string;
    text: string;
  };
  message: string;
}

export interface IGQLParsedErrorCode {
  errorCode: GraphQLErrorCodes;
  message?: string;
}

/**
 * Given an error code from the CTG GQL api, return localized error dialog content.
 *
 * Returns `undefined` if we have no dialog defined for the given error code.
 * @param parsedError RBI CTG GraphQL error code
 * @param staticStrings Defined static strings
 */
export function getErrorDialogForGraphQLCode(
  parsedError: IGQLParsedErrorCode,
  formatMessage: IntlFormatters['formatMessage']
): IErrorDialog | undefined {
  const { errorCode, message } = parsedError;
  // @todo Define this mapping outside of function
  const mapping = {
    [GraphQLErrorCodes.PAYMENT_NOT_SUPPORTED]: {
      title: formatMessage({ id: 'paymentNotSupportedHeading' }),
      body: formatMessage({ id: 'paymentNotSupported' }),
      message: formatMessage({ id: 'paymentNotSupported' })
    },
    [GraphQLErrorCodes.PAYMENTS_DECLINED_ERROR]: {
      title: formatMessage({ id: 'paymentDeclinedHeading' }),
      body: formatMessage({ id: 'paymentDeclined' }),
      message: formatMessage({ id: 'paymentDeclined' })
    },
    [GraphQLErrorCodes.PAYMENTS_DETAILS_ERROR]: {
      title: formatMessage({ id: 'paymentDetailsErrorHeading' }),
      body: formatMessage({ id: 'paymentDetailsError' }),
      message: formatMessage({ id: 'paymentDetailsError' })
    },
    [GraphQLErrorCodes.PAYMENTS_ERROR]: {
      title: formatMessage({ id: 'paymentDeclinedHeading' }),
      body: formatMessage({ id: 'paymentDeclined' }),
      message: formatMessage({ id: 'paymentDeclined' })
    },
    [GraphQLErrorCodes.GRAPHQL_VALIDATION_FAILED]: {
      title: formatMessage({ id: 'validationErrorHeading' }),
      body: message || formatMessage({ id: 'validationError' }),
      message: message || formatMessage({ id: 'validationError' })
    },
    [GraphQLErrorCodes.TOO_MANY_REQUESTS]: {
      title: formatMessage({ id: 'duplicateSupportFormSubmission' }),
      body: formatMessage({ id: 'duplicateSupportFormSubmission' }),
      message: formatMessage({ id: 'duplicateSupportFormSubmission' })
    }
  };

  return mapping[errorCode];
}

export const getAppleOrGooglePayError = (fdAccountId: string, defaultMessage?: string) => {
  // Send custom message if it's apple pay or google pay
  // Otherwise, show default message
  const messageMapping = {
    [CardTypes.APPLE_PAY]: 'orderFailureMessageForApplePay',
    [CardTypes.GOOGLE_PAY]: 'orderFailureMessageForGooglePay'
  };

  return messageMapping[fdAccountId] || defaultMessage;
};

/**
 * Creates a dictionary from an array of gql errors using code as the lookup index
 * If a code is not supplied the key will be `GQL_ERROR_$index`
 *
 * @param {GQLError[]} gqlErrors
 *
 * @returns {IGraphqlErrorMap}
 */
export const createGqlErrorMap = (gqlErrors: GQLError[]): IGraphqlErrorMap =>
  gqlErrors.reduce((acc, error, idx) => {
    const key = `${error.extensions?.code || 'GQL_ERROR'}_${idx}`;
    acc[key] = error;
    return acc;
  }, {});

export * from './types';
